import { useState } from 'react';
import { Link } from 'react-router';
import { ChevronDown, ChevronUp, FolderPlus, Plus, Trash2 } from 'lucide-react';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import { createGroup, deleteGroup, deleteHabit, moveHabit, updateGroup } from '../domain/actions';
import { HABIT_COLORS, type GroupRec, type HabitRec } from '../domain/model';
import { HabitIcon } from '../ui/HabitIcon';
import { Segmented, Field } from '../ui/Field';
import { Sheet } from '../ui/Sheet';
import { Empty } from '../ui/Empty';
import { useToast } from '../ui/Toast';

type Tab = 'active' | 'archived' | 'trash';

export function HabitsScreen() {
  const { t } = useI18n();
  const { data } = useApp();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('active');
  const [query, setQuery] = useState('');
  const [groupSheet, setGroupSheet] = useState<GroupRec | 'new' | null>(null);

  const sorted = [...data.habits].sort((a, b) => (a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0));
  const list = sorted.filter((h) =>
    tab === 'trash' ? Boolean(h.deletedAt) : !h.deletedAt && (tab === 'archived' ? Boolean(h.archivedAt) : !h.archivedAt),
  );
  const q = query.trim().toLowerCase();
  const filtered = q ? list.filter((h) => h.name.toLowerCase().includes(q)) : list;
  const groups = data.groups.filter((g) => !g.deletedAt).sort((a, b) => (a.sortKey < b.sortKey ? -1 : 1));

  const move = async (h: HabitRec, dir: -1 | 1) => {
    const all = sorted.filter((x) => !x.deletedAt);
    const i = all.findIndex((x) => x.id === h.id);
    const j = i + dir;
    if (j < 0 || j >= all.length) return;
    // Nueva posición entre los vecinos del destino.
    const before = dir === -1 ? all[j - 1]?.sortKey ?? null : all[j]!.sortKey;
    const after = dir === -1 ? all[j]!.sortKey : all[j + 1]?.sortKey ?? null;
    try {
      await moveHabit(h.id, before, after);
    } catch {
      toast.show(t('errors.save'));
    }
  };

  return (
    <div className="stack-l">
      <header className="page-header" style={{ marginBottom: 0 }}>
        <h1>{t('habits.title')}</h1>
        <Link to="/habits/new" className="btn btn--primary">
          <Plus size={20} aria-hidden="true" /> {t('common.add')}
        </Link>
      </header>

      <Segmented
        legend={t('habits.title')}
        value={tab}
        onChange={setTab}
        options={[
          { value: 'active', label: t('habits.active') },
          { value: 'archived', label: t('habits.archived') },
          { value: 'trash', label: t('habits.trash') },
        ]}
      />

      {list.length > 4 && (
        <Field label={t('habits.search')}>
          {(p) => <input {...p} type="search" className="input" value={query} onChange={(e) => setQuery(e.target.value)} />}
        </Field>
      )}

      {tab === 'trash' && list.length > 0 && <p className="muted small">{t('habits.trashHelp')}</p>}

      {list.length === 0 ? (
        tab === 'active' ? (
          <Empty
            title={t('today.emptyTitle')}
            body={t('today.emptyBody')}
            action={
              <Link to="/habits/new" className="btn btn--primary">
                {t('today.emptyCta')}
              </Link>
            }
          />
        ) : (
          <p className="muted">{t('habits.empty')}</p>
        )
      ) : filtered.length === 0 ? (
        <p className="muted">{t('habits.noResults')}</p>
      ) : (
        <ul className="list-card" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {filtered.map((h, i) => (
            <li key={h.id} className="list-item" data-color={h.color}>
              <div className="habit__icon">
                <HabitIcon name={h.icon} />
              </div>
              {tab === 'trash' ? (
                <div className="list-item__body">
                  <div className="list-item__title">{h.name}</div>
                  <div className="list-item__sub">{t(`habit.kinds.${h.kind}`)}</div>
                </div>
              ) : (
                <Link to={`/habits/${h.id}`} className="list-item__body" style={{ color: 'inherit', textDecoration: 'none' }}>
                  <div className="list-item__title">{h.name}</div>
                  <div className="list-item__sub">
                    {t(`habit.kinds.${h.kind}`)}
                    {h.primaryGroupId && groups.find((g) => g.id === h.primaryGroupId) ? ` · ${groups.find((g) => g.id === h.primaryGroupId)!.name}` : ''}
                  </div>
                </Link>
              )}
              {tab === 'trash' ? (
                <button type="button" className="btn btn--sm" onClick={() => void deleteHabit(h, false)}>
                  {t('common.restore')}
                </button>
              ) : (
                !q && (
                  <>
                    <button type="button" className="icon-btn" disabled={i === 0} onClick={() => void move(h, -1)} aria-label={t('habits.moveUp', { name: h.name })}>
                      <ChevronUp size={20} aria-hidden="true" />
                    </button>
                    <button type="button" className="icon-btn" disabled={i === filtered.length - 1} onClick={() => void move(h, 1)} aria-label={t('habits.moveDown', { name: h.name })}>
                      <ChevronDown size={20} aria-hidden="true" />
                    </button>
                  </>
                )
              )}
            </li>
          ))}
        </ul>
      )}

      <section className="stack" aria-label={t('habits.groups')}>
        <div className="row">
          <h2 className="spacer">{t('habits.groups')}</h2>
          <button type="button" className="btn btn--sm" onClick={() => setGroupSheet('new')}>
            <FolderPlus size={18} aria-hidden="true" /> {t('habits.newGroup')}
          </button>
        </div>
        {groups.length > 0 && (
          <div className="list-card">
            {groups.map((g) => (
              <button key={g.id} type="button" className="list-item" data-color={g.color} onClick={() => setGroupSheet(g)}>
                <span className="legend__dot" style={{ background: 'var(--hc)', width: 14, height: 14 }} aria-hidden="true" />
                <span className="list-item__body">
                  <span className="list-item__title">{g.name}</span>
                  <span className="list-item__sub" style={{ display: 'block' }}>
                    {data.habits.filter((h) => !h.deletedAt && (h.primaryGroupId === g.id || h.secondaryGroupIds.includes(g.id))).length}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      <GroupSheet group={groupSheet} onClose={() => setGroupSheet(null)} />
    </div>
  );
}

function GroupSheet({ group, onClose }: { group: GroupRec | 'new' | null; onClose: () => void }) {
  const { t } = useI18n();
  const { data } = useApp();
  const toast = useToast();
  const editing = group && group !== 'new' ? group : null;
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>('teal');
  const [lastKey, setLastKey] = useState<string | null>(null);
  const key = group === 'new' ? 'new' : (editing?.id ?? null);
  if (key !== lastKey) {
    setLastKey(key);
    setName(editing?.name ?? '');
    setColor(editing?.color ?? 'teal');
  }

  return (
    <Sheet open={group !== null} onClose={onClose} title={editing ? editing.name : t('habits.newGroup')}>
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim()) return;
          try {
            if (editing) await updateGroup(editing.id, { name: name.trim(), color });
            else await createGroup(name, color, data.groups);
            onClose();
          } catch {
            toast.show(t('errors.save'));
          }
        }}
      >
        <Field label={t('habits.groupName')}>
          {(p) => <input {...p} className="input" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} autoFocus />}
        </Field>
        <div className="swatches" role="group" aria-label={t('editor.color')}>
          {HABIT_COLORS.map((c) => (
            <button key={c} type="button" className="swatch" data-color={c} aria-pressed={color === c} aria-label={c} onClick={() => setColor(c)} />
          ))}
        </div>
        <button type="submit" className="btn btn--primary btn--block" disabled={!name.trim()}>
          {t('common.save')}
        </button>
        {editing && (
          <>
            <p className="muted small">{t('habits.deleteGroupBody')}</p>
            <button
              type="button"
              className="btn btn--danger btn--block"
              onClick={async () => {
                await deleteGroup(editing.id, data.habits);
                onClose();
              }}
            >
              <Trash2 size={18} aria-hidden="true" /> {t('habits.deleteGroup')}
            </button>
          </>
        )}
      </form>
    </Sheet>
  );
}
